import { useEffect } from 'react'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import { restoreActiveConversion } from 'Pods/ThreeDStudio/Store/actions/export'
import { addActiveRun } from 'Pods/ThreeDStudio/Store/actions/generation'
import { GENERATION_TRIGGER_KEY } from 'Pods/ThreeDStudio/Store/constants'

const toStartedAt = (createdAt) => {
  const parsed = createdAt ? new Date(createdAt).getTime() : NaN
  return Number.isNaN(parsed) ? Date.now() : parsed
}

export default function usePendingGeneratorRun() {
  const { getData } = useApiGet()
  const { isCurrentUser: isLoggedIn } = usePageConfig() || {}

  useEffect(() => {
    if (isLoggedIn !== true) {
      return
    }

    getData({
      path: '/api/internal/generator/runs/pending',
      onSuccess: (response) => {
        const sessions = response.data?.data || []

        // Every in-flight run is restored (paid tiers can run several concurrently);
        // each gets its own activeRuns entry and channel subscription.
        sessions.forEach(({ session, runs }) => {
          (runs || []).forEach(({
            id: runId,
            created_at: createdAt,
            providerModelId,
            createdNewSession,
          }) => {
            if (runId == null) {
              return
            }

            // A run that did not create a new session joined an existing one, so it adds a
            // variation to that session: an enhancement, or a re-generation from an edited
            // reference image. Both restore the "Processing..." overlay on that session's
            // card instead of a "Generating..." card of their own (LibraryContent falls
            // back to a card when the session has none - an image-assistant session holds
            // no model yet, so it is not in the library list). Only an enhancement resumes
            // the in-progress label on its sidebar tab's button; a re-generation belongs to
            // the main generate button, like an initial generation (new session).
            const variationType = VARIATION_TYPES[providerModelId]
            const isVariation = createdNewSession === false
            const isEnhancement = isVariation && !!variationType?.isEnhancement
            addActiveRun({
              clientId: `pending-run-${runId}`,
              runId,
              sessionId: session,
              startedAt: toStartedAt(createdAt),
              isEnhancement,
              enhancingSessionId: isVariation ? session : undefined,
              triggeredBy: isEnhancement ? (variationType.tab ?? null) : GENERATION_TRIGGER_KEY,
              // A restored run has no `settings` (that snapshot only exists in the tab that
              // started it), so keep the provider model id: it is the other half of the
              // payload setOpenedModel needs when the user opens this run's card.
              providerModelId,
            })
          })
        })

        const activeConversion = response.data?.active_conversion
        if (activeConversion) {
          restoreActiveConversion(activeConversion)
        }
      },
    })
  }, [getData, isLoggedIn])
}
